import { useState, useContext, useEffect, useRef } from 'react'
import WizardDataContext from '../../../store/WizardDataContext';

import { Input } from "@mui/material";
import couponCodeImg from "../../../assets/img/ad/coupon_code.svg"

export default function CouponCodeAndDetails({ setDisableBtn }) {

  // URL validation regex (no global flag to avoid stateful .test issues)
  const urlRegex = /^(https?|ftp):\/\/(([a-z\d]([a-z\d-]*[a-z\d])?\.)+[a-z]{2,}|localhost)(\/[-a-z\d%_.~+]*)*(\?[;&a-z\d%_.~+=-]*)?(#[-a-z\d_]*)?$/i;

  const wizardCtx = useContext(WizardDataContext);
  const { wizardData, updateData } = wizardCtx;
  const couponDetails = wizardData.couponDetails;
  const company = wizardData.company;

  const [websiteNotValid, setWebsiteNotValid] = useState(false)
  const [codeNotValid, setCodeNotValid] = useState(false)
  const [phoneNotValid, setPhoneNotValid] = useState(false)
  const [emailNotValid, setEmailNotValid] = useState(false)
  const [descriptionNotValid, setDescriptionNotValid] = useState(false)
  const [showAtLeastOneError, setShowAtLeastOneError] = useState(false)
  // Prefill from step 1 only once on load; after that user can clear email/website/phone without them coming back
  const hasPrefilledFromCompanyRef = useRef(false)

  useEffect(() => {

    if (couponDetails.code?.trim() !== '') {
      if (/^[0-9A-Z]+$/mg.test(couponDetails.code)) {
        setCodeNotValid(false)
      } else {
        setCodeNotValid(true)
      }
    } else {
      setCodeNotValid(false)
    }

  }, [couponDetails.code])

  useEffect(() => {

    if (couponDetails.website?.trim() !== '' && couponDetails.website !== 'https://') {
      if (urlRegex.test(couponDetails.website)) {
        setWebsiteNotValid(false)
      } else {
        setWebsiteNotValid(true)
      }
    } else {
      setWebsiteNotValid(false)
    }

  }, [couponDetails.website])

  useEffect(() => {
    const phoneRegex = /^[+]?[0-9\s\-\(\)]{10,}$/;
    if (couponDetails.phone?.trim() !== '') {
      if (phoneRegex.test(couponDetails.phone)) {
        setPhoneNotValid(false)
      } else {
        setPhoneNotValid(true)
      }
    } else {
      setPhoneNotValid(false)
    }
  }, [couponDetails.phone])

  useEffect(() => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (couponDetails.email?.trim() !== '') {
      if (emailRegex.test(couponDetails.email)) {
        setEmailNotValid(false)
      } else {
        setEmailNotValid(true)
      }
    } else {
      setEmailNotValid(false)
    }
  }, [couponDetails.email])

  useEffect(() => {
    if (couponDetails.description?.trim() !== '' && couponDetails.description?.length <= 100) {
      setDescriptionNotValid(false)
    } else {
      setDescriptionNotValid(true)
    }
  }, [couponDetails.description])

  useEffect(() => {
    // Prefill from step 1 only once when the step loads; then user can clear any of the three without them repopulating
    if (hasPrefilledFromCompanyRef.current) return;

    const hasNoWebsite = !couponDetails.website || couponDetails.website.trim() === '' || couponDetails.website === 'https://';
    const hasCompanyData = company.name || company.email || company.managerName;
    const companyWebsiteExists = company.website &&
        company.website.trim() !== '' &&
        company.website !== 'https://';
    const shouldPrefillWebsite = hasCompanyData && companyWebsiteExists && hasNoWebsite;
    const shouldPrefillPhone = company.managerNumber && !couponDetails.phone;
    const shouldPrefillEmail = company.email && !couponDetails.email;

    const updates = { ...couponDetails };
    let hasUpdates = false;

    if (shouldPrefillWebsite) {
      updates.website = company.website;
      hasUpdates = true;
    } else if (hasNoWebsite && (!company.website || company.website.trim() === '' || company.website === 'https://') &&
          couponDetails.website !== 'https://') {
      updates.website = 'https://';
      hasUpdates = true;
    }
    if (shouldPrefillPhone) {
      updates.phone = company.managerNumber;
      hasUpdates = true;
    }
    if (shouldPrefillEmail) {
      updates.email = company.email;
      hasUpdates = true;
    }

    if (hasUpdates) {
      hasPrefilledFromCompanyRef.current = true;
      updateData('couponDetails', updates);
    } else if (hasCompanyData) {
      // Step 1 data already present; don't prefill again when user clears a field later
      hasPrefilledFromCompanyRef.current = true;
    }
  }, [company.website, couponDetails, company, updateData])

  useEffect(() => {
    const isCouponCodeValid = couponDetails.code?.trim() !== '' && /^[0-9A-Z]+$/mg.test(couponDetails.code);
    const isCouponWebsiteValid = couponDetails.website?.trim() !== '' && couponDetails.website !== 'https://' && urlRegex.test(couponDetails.website);
    const phoneRegex = /^[+]?[0-9\s\-\(\)]{10,}$/;
    const isCouponPhoneValid = couponDetails.phone?.trim() !== '' && phoneRegex.test(couponDetails.phone);
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isCouponEmailValid = couponDetails.email?.trim() !== '' && emailRegex.test(couponDetails.email);
    const isDescriptionValid = couponDetails.description?.trim() !== '' && couponDetails.description?.length <= 100;
    
    // Check if any contact field has been filled
    const hasWebsite = couponDetails.website && couponDetails.website.trim() !== '' && couponDetails.website !== 'https://';
    const hasPhone = couponDetails.phone && couponDetails.phone.trim() !== '';
    const hasEmail = couponDetails.email && couponDetails.email.trim() !== '';
    
    // If a field is filled, it must be valid. If empty, it's considered valid (optional field)
    const websiteFieldValid = !hasWebsite || isCouponWebsiteValid;
    const phoneFieldValid = !hasPhone || isCouponPhoneValid;
    const emailFieldValid = !hasEmail || isCouponEmailValid;
    
    // Code and Description are required + at least one other field (website, phone, or email) must be valid
    const hasAtLeastOneOtherField = isCouponWebsiteValid || isCouponPhoneValid || isCouponEmailValid;
    const allContactFieldsValid = websiteFieldValid && phoneFieldValid && emailFieldValid;
    const allFieldsValid = isCouponCodeValid && isDescriptionValid && hasAtLeastOneOtherField && allContactFieldsValid;
    
    // Show error if code and description are filled but no contact method
    const hasAttemptedContactInfo = hasWebsite || hasPhone || hasEmail;
    
    setShowAtLeastOneError(isCouponCodeValid && isDescriptionValid && !hasAttemptedContactInfo && !hasAtLeastOneOtherField);
    
    setDisableBtn(!allFieldsValid);
    
  }, [couponDetails.code, couponDetails.website, couponDetails.phone, couponDetails.email, couponDetails.description, setDisableBtn])


  const handleChangeCode = (e) => {
    // Convert input to uppercase automatically
    const value = e.target.value.toUpperCase();
    updateData('couponDetails', { code: value })
  }


  const handleChangeWebsite = (e) => {
    // let value = e.target.value
    // setCouponWebsite(value)
    updateData('couponDetails', { website: e.target.value })
  }

  const handleChangePhone = (e) => {
    updateData('couponDetails', { phone: e.target.value })
  }

  const handleChangeEmail = (e) => {
    updateData('couponDetails', { email: e.target.value })
  }

  return (
    <>
      <div className="step5-container">
        <div className="heading" style={{ marginBottom: '1.5rem' }}>
          <h1>Coupon Code &amp; Details</h1>
        </div>
        {/* Top row: Coupon Code and Coupon Details side by side */}
        <div className="step5-top-row">
          <div className="step5-part1">
            <div className="heading">
              <h1>Coupon Code</h1>
              <h3>Enter your coupon code for this offer</h3>
            </div>
            <div className="image">
              <img src={couponCodeImg} alt="coupon code" />
            </div>
            <div className="input">
              <label htmlFor="couponCode">COUPON CODE</label>
              <Input error={codeNotValid} value={couponDetails.code} id="couponCode" placeholder="Ex: DELIVA2020" onChange={(e) => handleChangeCode(e)} />
              {codeNotValid ? <span className="helper-text" style={{ color: '#d32f2f' }}>It must be ALL CAPS without any spaces or <br /> special characters.</span> : <span  className="helper-text">&nbsp;</span>}
            </div>
          </div>

          <div className="step5-part2">
            <div className="heading">
              <h1>Coupon Details</h1>
              <h3>Enter relevant contact details below</h3>
            </div>
            <div className="input">
              <label htmlFor="couponWebsite">WEBSITE</label>
              <Input error={websiteNotValid} value={couponDetails.website} id="couponWebsite" placeholder="Ex: http://deliva.com" onChange={(e) => handleChangeWebsite(e)} />
              {websiteNotValid ? <span className="helper-text" style={{ color: '#d32f2f' }}>Include full address with http://</span> : <span  className="helper-text">&nbsp;</span>}
            </div>
            <div className="input">
              <label htmlFor="couponPhone">PHONE NUMBER</label>
              <Input error={phoneNotValid} value={couponDetails.phone} id="couponPhone" placeholder="Ex: 9876543210" onChange={(e) => handleChangePhone(e)} />
              {phoneNotValid ? <span className="helper-text" style={{ color: '#d32f2f' }}>Enter a valid phone number</span> : <span className="helper-text">&nbsp;</span>}
            </div>
            <div className="input">
              <label htmlFor="couponEmail">EMAIL ADDRESS</label>
              <Input error={emailNotValid} value={couponDetails.email} id="couponEmail" placeholder="Ex: contact@deliva.com" onChange={(e) => handleChangeEmail(e)} />
              {emailNotValid ? <span className="helper-text" style={{ color: '#d32f2f' }}>Enter a valid email address</span> : showAtLeastOneError ? <span className="helper-text" style={{ color: '#d32f2f' }}>Please enter at least one contact method (website, phone, or email)</span> : <span className="helper-text">&nbsp;</span>}
            </div>
          </div>
        </div>

        {/* Bottom section: Coupon Description */}
        <div className="step5-bottom-section">
          <div className="heading">
            <h1>Coupon Description</h1>
            <h3>Provide a brief description of your promotional offer</h3>
          </div>
          <div className="input">
            <textarea
              id="couponDescription"
              value={couponDetails.description}
              onChange={e => updateData('couponDetails', { description: e.target.value })}
              placeholder="Brief specific description of the deal."
              maxLength={100}
            />
            <div className="coupon-description-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
              <span style={{ color: (couponDetails.description?.length || 0) >= 100 ? '#f00' : '#6f7f9f', fontSize: 14 }}>
                {couponDetails.description?.length ?? 0}/100
              </span>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
